import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';
import { Subscription } from 'rxjs';

/**
 * "Cargar cambio" (replenish from entrance) modal for a smart drawer.
 *
 * Carga de monedas/billetes DESDE LA ENTRADA del cajón hacia el reciclador,
 * con dos pasos manuales — el operario deja el efectivo en la boca y decide
 * cuándo cerrar:
 *
 *   START_REPLENISH → (el operario introduce el cambio) → FINISH_REPLENISH
 *
 * La respuesta del FINISH trae `totalDeposited` (lo que se acaba de cargar),
 * que mostramos al cerrar. Es agnóstico del cajón: cada driver mapea estos
 * comandos a lo suyo (PROSEGUR StartReplenish/FinishReplenish; GLORY
 * StartReplenishmentFromEntrance/EndReplenishmentFromEntrance). En PROSEGUR
 * además hay `CHECK_REPLENISH`, con el que refrescamos el total en vivo.
 *
 * El padre (`ui-tab-cash`) conecta y pasa la conexión + target por
 * `componentProps`, y desconecta al hacer dismiss.
 */
@Component({
    selector: 'ui-modal-replenish',
    templateUrl: './ui-replenish.html',
    styleUrls: [ './ui-replenish.scss' ]
})
export class UiReplenishModal extends languageSupport implements OnInit, OnDestroy {
    /** Live connection to the drawer, supplied by the parent. */
    public _connection: WSConnection = null;
    /** WS target = drawer model (`PROSEGUR` / `GLORY`). */
    public _target: string = 'PROSEGUR';

    public status: 'loading' | 'finishing' | 'success' | 'error' = 'loading';
    public currentAmount: number = 0;   // euros cargados en vivo (solo PROSEGUR)
    public totalLoaded: number = 0;      // euros cargados (respuesta del FINISH)
    // Desglose de lo cargado por denominación (GLORY): [{label, pieces}].
    public loadedRows: Array<{ label: string, pieces: number }> = [];
    public errorMessage: string = null;

    private _opId: string = null;         // operationId compartido de la recarga
    private _open: boolean = false;       // la recarga está abierta en el cajón
    private _finishing: boolean = false;  // evita doble FINISH
    private _cancelled: boolean = false;
    private _sub: Subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'PROSEGUR';
    }

    // Solo PROSEGUR expone CHECK_REPLENISH (total en vivo). GLORY es Start→End.
    private get _supportsCheck(): boolean {
        return (this._target || '').toUpperCase() === 'PROSEGUR';
    }

    get PhaseLabel(): string {
        switch (this.status) {
            case 'loading':    return this.tr('@smartdrawer_replenish_insert');
            case 'finishing':  return this.tr('@smartdrawer_replenish_finishing');
            case 'success':    return this.tr('@smartdrawer_replenish_done');
            case 'error':      return this.errorMessage || this.tr('@smartdrawer_replenish_error');
            default:           return '';
        }
    }

    // El botón "Finalizar" solo mientras la carga está abierta.
    get CanFinish(): boolean {
        return this.status === 'loading' && this._open && !this._finishing;
    }

    async ngOnInit() {
        if (!this._connection) {
            this._fail(this.tr('@cashguard_connection_failed'));
            return;
        }

        this._sub = this._connection.OnMessageRecv.subscribe(data => this._onFrame(data));
        this._opId = 'rpl' + Date.now();

        try {
            // Abrir la carga desde la entrada.
            if (!this._ok(await this._send('START_REPLENISH', { operationId: this._opId }))){
                this._fail();
                return;
            }
            this._open = true;
            this.status = 'loading';
            this.change.markForCheck();

            // PROSEGUR: refrescar el total en vivo mientras el operario mete cambio.
            if (this._supportsCheck){
                this._pollCheck();
            }
        } catch (e) {
            this._fail();
        }
        this.change.markForCheck();
    }

    ngOnDestroy() {
        super.OnDestroy();
        this._cancelled = true;
        if (this._sub){
            this._sub.unsubscribe();
            this._sub = null;
        }
        // Salvaguarda: nunca dejar la recarga abierta si la modal muere a media.
        if (this._open){
            this._open = false;
            this._cancelReplenish();
        }
    }

    // Cierra la carga y muestra lo cargado (totalDeposited del FINISH).
    async OnFinish() {
        if (!this.CanFinish){
            return;
        }
        this._finishing = true;
        this.status = 'finishing';
        this.change.markForCheck();
        try {
            const fin = await this._send('FINISH_REPLENISH', { operationId: this._opId });
            if (!this._ok(fin)){
                // FINISH falló: NO marcar cerrado. Dejamos `_open=true` para que
                // la salvaguarda (ngOnDestroy/OnCancel) emita CANCEL_REPLENISH y
                // no deje la operación abierta con el dinero cargado bloqueado.
                this._fail();
                return;
            }
            this._open = false;   // solo tras un FINISH correcto
            const _fbody = this._body(fin);
            this.totalLoaded = this._num(_fbody, 'totalDeposited', 0) / 100;
            // Desglose por denominación (GLORY reporta loadedDenominations).
            const _ld = _fbody && _fbody['loadedDenominations'];
            if (Array.isArray(_ld)){
                this.loadedRows = _ld
                    .map((d: any) => ({
                        label: (this._num(d, 'denomination', 0) / 100).toFixed(2),
                        pieces: this._num(d, 'numItems', 0)
                    }))
                    .filter(r => r.pieces > 0);
            }
            this.status = 'success';
        } catch (e) {
            // Timeout/excepción en el FINISH: dejamos `_open=true` para que la
            // salvaguarda cancele y reconcilie (no dejar el dinero bloqueado).
            this._fail();
        }
        this.change.markForCheck();
    }

    // Aborta la carga en curso (devuelve a la boca lo no almacenado).
    async OnCancel() {
        if (this._finishing){
            return;
        }
        this._cancelled = true;
        if (this._open){
            this._open = false;
            await this._cancelReplenish();
        }
        this.modalCtrl.dismiss();
    }

    OnClose() {
        // Devolvemos lo cargado (euros) para que quien abrió la recarga lo registre
        // en el arqueo como ENTRADA de efectivo. 0 si no llegó a finalizar.
        this.modalCtrl.dismiss({ totalLoaded: this.totalLoaded || 0 });
    }

    /*****************************/
    /* INTERNALS                 */
    /*****************************/

    /** Refresca `currentAmount` con CHECK_REPLENISH hasta cerrar/cancelar. */
    private async _pollCheck() {
        const _pollMs = 1000;
        while (this._open && !this._cancelled && !this._finishing){
            try {
                const resp = await this._send('CHECK_REPLENISH', { operationId: this._opId });
                const amount = this._num(this._body(resp), 'totalDeposited', -1);
                if (amount >= 0){
                    this.currentAmount = amount / 100;
                    this.change.markForCheck();
                }
            } catch (_) { /* transitorio; seguimos */ }
            await this._sleep(_pollMs);
        }
    }

    private async _cancelReplenish() {
        try { await this._send('CANCEL_REPLENISH', { operationId: this._opId }); } catch (_) {}
    }

    private _send(command: string, extra?: any): Promise<any> {
        return this._connection.Send(this._target, Object.assign({ command }, extra || {}));
    }

    private _ok(resp: any): boolean {
        const body = this._body(resp);
        return !!(resp && resp.exec === 'SUCCESS' && (!body || body['status'] !== 'error'));
    }

    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@smartdrawer_replenish_error');
        this.change.markForCheck();
    }

    /** Frames en vivo (GLORY puede emitir progreso de depósito por evento). */
    private _onFrame(msg: any) {
        const body = this._body(msg);
        if (!body || !body['status']){
            return;
        }
        if (body['status'] === 'progress' && body['currentAmount'] !== undefined && this._open){
            this.currentAmount = (body['currentAmount'] || 0) / 100;
            this.change.markForCheck();
        }
    }
}
